import { useEffect, useMemo, useState } from 'react'

import InfoIcon from '@mui/icons-material/Info'
import { Box, Button, Grid, Paper, Skeleton, Stack, Typography } from '@mui/material'
import { green, red } from '@mui/material/colors'
import { GridCellParams, GridRowModel, GridSortItem } from '@mui/x-data-grid'
import clsx from 'clsx'
import { format, parseJSON } from 'date-fns'
import GoogleMapReact from 'google-map-react'

import DriverIcon from 'components/bookings/MapRoute/DriverIcon'
import { EtaSourceTooltip, FplDataGrid, IGridColumn } from 'components/common'
import { DATE_TIME_FORMAT, TRACKING_DASHBOARD_POLLING_INTERVAL_MS } from 'constants/index'
import {
  GetDriversLocationTrackingGridQuery,
  useGetDriverNextRouteLazyQuery,
} from 'generated/graphql'
import { GET_DRIVERS_LOCATION_TRACKING_GRID } from 'graphql/queries'
import { Restricted } from 'providers'

import DriverTrackingExportDialog from './DriverTrackingExportDialog'

interface IDriverRoute {
  id: string
  longitude: number
  latitude: number
  placeId: string | null | undefined
}

const BRISTOL_COORDINATES = { lat: 51.45, lng: -2.59 }
const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? ''

const columns: IGridColumn[] = [
  {
    field: 'id',
    headerName: 'Driver ID',
    sortPath: ['id'],
    flex: 0.1,
    minWidth: 90,
  },
  {
    field: 'driverName',
    headerName: 'Driver Name',
    sortPath: ['name'],
    flex: 0.2,
    minWidth: 150,
    valueGetter(params) {
      return params.row.name
    },
  },
  {
    field: 'lastTrackedAt',
    headerName: 'Last Tracked At',
    sortPath: ['location', 'lastModifiedAt'],
    sortable: true,
    flex: 0.2,
    minWidth: 150,
    valueGetter(params) {
      const date = params.row.location?.lastModifiedAt ?? params.row.location?.createdAt
      return date ? format(parseJSON(date as string), DATE_TIME_FORMAT) : 'N/A'
    },
  },
  {
    field: 'allocated',
    headerName: 'Allocated',
    sortable: false,
    flex: 0.1,
    minWidth: 110,
    valueGetter(params) {
      return params.row.driverTrackingStatus?.allocated ?? 'N/A'
    },
  },
  {
    field: 'nextStopPostCode',
    headerName: 'Next Stop Post Code',
    sortable: false,
    flex: 0.2,
    minWidth: 140,
    valueGetter(params) {
      return params.row.driverTrackingStatus?.nextStopPostCode ?? 'N/A'
    },
  },
  {
    field: 'nextStopDateTime',
    headerName: 'Next Stop Date Time',
    sortable: false,
    flex: 0.2,
    minWidth: 150,
    valueGetter(params) {
      const date = params.row.driverTrackingStatus?.nextStopDateTime
      return date ? format(parseJSON(date as string), DATE_TIME_FORMAT) : 'N/A'
    },
  },
  {
    field: 'nextStopLiveEta',
    headerName: 'Next Stop ETA',
    cellClassName: (params: GridCellParams<GridRowModel>) =>
      clsx({
        onTime:
          params.row.driverTrackingStatus?.nextStopLiveEta &&
          params.row.driverTrackingStatus?.nextStopLiveEta <=
            params.row.driverTrackingStatus?.nextStopDateTime,
        late:
          params.row.driverTrackingStatus?.nextStopLiveEta &&
          params.row.driverTrackingStatus?.nextStopLiveEta >
            params.row.driverTrackingStatus?.nextStopDateTime,
      }),
    sortable: false,
    flex: 1,
    minWidth: 600,
    renderCell(params) {
      const {
        nextStopLiveEtaSource,
        nextStopLiveEtaLastUpdatedAt,
        nextStopLiveEta,
        stopName,
        stopAddress,
      } = params.row.driverTrackingStatus ?? {}
      const etaText = nextStopLiveEta ? format(parseJSON(nextStopLiveEta), DATE_TIME_FORMAT) : 'N/A'

      return (
        <Stack>
          <EtaSourceTooltip
            source={nextStopLiveEtaSource}
            lastUpdatedAt={nextStopLiveEtaLastUpdatedAt}
            hasValue={!!nextStopLiveEta}
            tabIndex={params.tabIndex}>
            {etaText}
          </EtaSourceTooltip>
          {stopName && stopAddress && (
            <Stack direction='row' spacing={0.5} alignItems='center'>
              <InfoIcon color='primary' sx={{ fontSize: 14 }} />
              <Typography variant='caption' noWrap>
                {`${stopName} — ${stopAddress}`}
              </Typography>
            </Stack>
          )}
        </Stack>
      )
    },
  },
]

function DriversTracking() {
  const [driverRoute, setDriverRoute] = useState<IDriverRoute>()
  const [selectedDriverId, setSelectedDriverId] = useState(-1)
  const [loading, setLoading] = useState(true)
  const [defaultMapCenter, setDefaultMapCenter] = useState(BRISTOL_COORDINATES)
  const [gridDrivers, setGridDrivers] = useState<GetDriversLocationTrackingGridQuery | undefined>()
  const [exportDialogOpen, setExportDialogOpen] = useState(false)

  // Queries
  const [getDriverNextRoute, { data: getDriverNextRouteData }] = useGetDriverNextRouteLazyQuery({
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (getDriverNextRouteData) {
      const defaultItem: IDriverRoute = {
        id: getDriverNextRouteData.driverNextRoute.id,
        placeId: getDriverNextRouteData.driverNextRoute.nextStopGoogleMapsPlaceId,
        latitude: getDriverNextRouteData.driverNextRoute.latitude,
        longitude: getDriverNextRouteData.driverNextRoute.longitude,
      }

      setDriverRoute(defaultItem)
      setDefaultMapCenter({
        lat: Number(getDriverNextRouteData.driverNextRoute.latitude),
        lng: Number(getDriverNextRouteData.driverNextRoute.longitude),
      })
    }
  }, [getDriverNextRouteData, selectedDriverId])

  const queryData = useMemo(
    () => ({
      query: GET_DRIVERS_LOCATION_TRACKING_GRID,
      entityName: 'driversLocationTracking',
      defaultSortOrder: {
        field: 'lastTrackedAt',
        sort: 'desc',
      } as GridSortItem,
    }),
    [],
  )

  // Handlers
  const handleApiLoaded = (map, maps) => {
    if (!driverRoute || selectedDriverId === -1) return
    const directionsService = new maps.DirectionsService()
    const directionsRenderer = new maps.DirectionsRenderer()
    directionsRenderer.setMap(map)
    let origin: string | null | undefined = ''
    let destination: { placeId: string | null | undefined } = { placeId: '' }
    const waypoints = [] as any

    origin = driverRoute?.latitude + ',' + driverRoute?.longitude
    destination = { placeId: driverRoute.placeId }

    if (!destination.placeId || destination.placeId === '') return
    const request = {
      origin,
      destination,
      waypoints,
      optimizeWaypoints: false,
      provideRouteAlternatives: true,
      travelMode: 'DRIVING',
      drivingOptions: {
        departureTime: new Date(),
      },
      unitSystem: maps.UnitSystem.IMPERIAL,
    }
    directionsService.route(request, (response, status) => {
      if (status === 'OK') {
        directionsRenderer.setDirections(response)
      }
    })
  }

  const handleRowClick = (gridRow) => {
    if (!gridRow.row.location) {
      return
    }
    getDriverNextRoute({
      variables: { driverId: gridRow.row.location.driverId },
      onCompleted: () => setSelectedDriverId(gridRow.row.location.driverId),
    })
  }

  const onDataLoadCompleteAction = (loadedDrivers, loading) => {
    setGridDrivers(loadedDrivers)
    setLoading(loading)
  }

  const onResetButtonClick = () => {
    setSelectedDriverId(-1)
  }

  const getDriverPinsLocations = () => {
    if (selectedDriverId === -1) {
      return gridDrivers?.driversLocationTracking?.edges?.map(
        (grid) =>
          grid.node?.location && (
            <DriverIcon
              key={grid.node?.id}
              lat={Number(grid.node.location.latitude)}
              lng={Number(grid.node.location.longitude)}
            />
          ),
      )
    }

    const driverLocation = gridDrivers?.driversLocationTracking?.edges?.find(
      (driver) => Number(driver.node?.id) === selectedDriverId,
    )?.node

    return (
      <DriverIcon
        key={driverLocation?.id}
        lat={Number(driverRoute?.latitude)}
        lng={Number(driverRoute?.longitude)}
      />
    )
  }

  return (
    <>
      <Grid container spacing={3} columns={8}>
        <Grid item xs={8} lg={4}>
          <Paper variant='outlined'>
            <FplDataGrid
              toolbar={{
                caption: 'Drivers Tracking',
                rightSide: (
                  <Restricted to={'DownloadAllReportTypes'}>
                    <Button
                      variant='contained'
                      sx={{ mt: 1, mb: 2 }}
                      onClick={() => setExportDialogOpen(true)}>
                      Export CSV
                    </Button>
                  </Restricted>
                ),
              }}
              isHideOldData
              refetchWithDelay
              pollInterval={TRACKING_DASHBOARD_POLLING_INTERVAL_MS}
              columns={columns}
              query={queryData.query}
              entityName={queryData.entityName}
              variableSortOrder={queryData.defaultSortOrder}
              onRowClick={handleRowClick}
              onDataLoadCompleteAction={onDataLoadCompleteAction}
              rootSx={{
                // overflowX: 'auto',
                '& .onTime': { backgroundColor: green[500] },
                '& .late': { backgroundColor: red[500] },
              }}
            />
          </Paper>
        </Grid>
        <Grid item xs={8} lg={4}>
          <Paper variant='outlined'>
            <Stack direction='row' justifyContent='space-between' padding={2}>
              <Typography variant='h6'>Live Tracking</Typography>
              <Button onClick={onResetButtonClick}>Reset</Button>
            </Stack>
            <Box height={{ xs: 500, lg: 730 }}>
              {!loading ? (
                <GoogleMapReact
                  key={selectedDriverId}
                  bootstrapURLKeys={{ key: API_KEY }}
                  defaultCenter={defaultMapCenter}
                  defaultZoom={5}
                  yesIWantToUseGoogleMapApiInternals
                  onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}>
                  {getDriverPinsLocations()}
                </GoogleMapReact>
              ) : (
                <Skeleton />
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
      <DriverTrackingExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
      />
    </>
  )
}

export default DriversTracking
